import { useCallback, useEffect, useRef, useState } from "react"
import { observer } from "mobx-react"
import { useStore, Contacts } from "@features/common"
import { RouterLink } from "@lib/routing"
import { LanguageSwitcher } from "@features/i18n"
import { BreakPoints } from "styles/styles"
import { mergeClasses } from "@lib/css"
import { isServer } from "@lib/isomorphic"
import classNames from "classnames"

type FooterProps = {
  googlePlayLink?: string | null
  appStoreLink?: string | null
  appGalleryLink?: string | null
  bgColor?: "black" | "gray-400"
  chinese?: boolean
  accessLanguages?: string[]
}

export const Footer = observer(function Footer(props: FooterProps) {
  const { GlobalModel, ResponsiveModel } = useStore()
  const {
    googlePlayLink,
    appStoreLink,
    appGalleryLink,
    bgColor = "gray-400",
    chinese,
    accessLanguages,
  } = props
  const currentYear = new Date().getFullYear()

  const companyName = "Online Classifieds AG, Switzerland"

  const isDesktop = ResponsiveModel.width >= BreakPoints.desktop
  const { pressLink, countryCode = "" } = GlobalModel
  const navbarRef = useRef<HTMLDivElement | null>(null)
  const showSharePersonalInfoButton =
    countryCode.toLowerCase() === "us" && !GlobalModel.isPureAppRu
  const server = isServer()
  const showPressLink = pressLink.enabled && countryCode.toLowerCase() !== "ru"

  const [isVisibleAppStore, setAppStoreVisibility] = useState(false)
  const [isVisibleGooglePlay, setGooglePlayVisibility] = useState(false)

  const calculateNavbarHeight = useCallback(() => {
    if (!navbarRef.current || server) return
    if (!showSharePersonalInfoButton && !showPressLink) {
      navbarRef.current!.classList.add(
        "grid",
        "grid-flow-col",
        "justify-between",
        "desktop:justify-end",
      )
      return
    }

    navbarRef.current!.classList.remove(
      "grid",
      "grid-flow-col",
      "justify-between",
      "desktop:justify-end",
    )

    const isTablet = window.innerWidth > 768 && window.innerWidth < 1280
    let tabletCount = 4
    let baseCount = 6

    if (!showSharePersonalInfoButton || !showPressLink) {
      tabletCount = 3
      baseCount = 5
    }

    const rowsCount = isTablet ? tabletCount : baseCount
    const columnsCount = isTablet ? 3 : 2

    const computedStyle = window.getComputedStyle(navbarRef.current!)
    const rg = parseInt(computedStyle.getPropertyValue("row-gap"))
    const pt = parseInt(computedStyle.getPropertyValue("padding-top"))
    const pb = parseInt(computedStyle.getPropertyValue("padding-bottom"))

    let height = 0

    for (let i = 0; i < columnsCount; i++) {
      let columnHeight = 0
      for (let t = i * rowsCount; t < (i + 1) * rowsCount; t++) {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        columnHeight += navbarRef.current!.children[t]?.clientHeight || 0
      }
      columnHeight += (rowsCount - 1) * rg + pt + pb + 2
      height = Math.max(height, columnHeight)
    }

    navbarRef.current!.style.maxHeight = `${height}px`
  }, [showSharePersonalInfoButton, showPressLink, server])

  useEffect(() => {
    if (isDesktop) {
      setAppStoreVisibility(true)
      setGooglePlayVisibility(true)
    } else if (ResponsiveModel.is_ios) {
      setAppStoreVisibility(true)
      setGooglePlayVisibility(false)
    } else if (ResponsiveModel.is_android) {
      setAppStoreVisibility(false)
      setGooglePlayVisibility(true)
    } else {
      setAppStoreVisibility(true)
      setGooglePlayVisibility(true)
    }
  }, [ResponsiveModel, isDesktop])

  useEffect(() => {
    calculateNavbarHeight()
  }, [calculateNavbarHeight, showPressLink, showSharePersonalInfoButton, server])

  useEffect(() => {
    window.addEventListener("resize", calculateNavbarHeight)

    return () => {
      window.removeEventListener("resize", calculateNavbarHeight)
    }
  }, [calculateNavbarHeight])

  useEffect(() => {
    setTimeout(() => calculateNavbarHeight(), 500)
  }, [calculateNavbarHeight, server, isDesktop])

  return (
    <footer
      className={mergeClasses(
        "relative",
        `bg-${bgColor} border-t-2 border-${bgColor}`,
      )}
    >
      <svg
        viewBox="0 0 375 8"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="none"
        className={mergeClasses(
          "absolute block w-full h-2 desktop:h-3 bottom-full border-0 outline-0",
          `text-${bgColor}`,
        )}
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M0 8H0.164716H375V1.71429L355.27 2.7968L338.199 1.87791L314.805 4.45378L291.315 2.7968L277.211 4.45378L264.292 1.87815L236.687 2.7968L209.084 4.00128L185.721 0.974023C185.721 0.974023 174.471 3.19026 169.5 3.19026C164.529 3.19026 162 0.974023 158.129 0.974023C154.259 0.974023 132.546 4.86924 132.546 4.86924L108.374 4.00128H85.715L41.9195 0L15.8503 2.7968L0 1.71429V8Z"
          fill="currentColor"
        />
      </svg>
      <div
        id="footer"
        data-test-id="footer"
        className="shrink-0 grow-0 text-white grid pt-14 px-4 tablet-m:px-10 desktop:px-20 pb-10 desktop:py-16 desktop:content-end"
      >
        <div className="footer__apps mb-7 tablet-m:mb-0 desktop:mb-8">
          <div className="font-serif text-xl leading-[30px] desktop:text-[18px] desktop:leading-6 text-center tablet-m:text-left mb-3">
            {GlobalModel.getTemplate("app.get", false)}
          </div>
          <div className="flex flex-col tablet-m:flex-row tablet-m:flex-wrap items-center">
            {isVisibleAppStore && (
              <a
                href={appStoreLink || GlobalModel.branch_links.app_store}
                data-test-id="footer__appstore_button"
                aria-label="app store"
                className="app-ios block border border-white hover:border-gold-300 rounded-lg text-white hover:text-gold-300 px-[9px] py-1.5 mb-3 tablet-m:mb-0 tablet-m:mr-3 desktop:mr-3"
              >
                <svg
                  className="block h-6.5"
                  viewBox="0 0 124 32"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18.9387 15.8603C18.9524 14.7921 19.2362 13.7446 19.7635 12.8155C20.2909 11.8864 21.0446 11.1057 21.9548 10.5462C21.3766 9.7205 20.6139 9.04097 19.7271 8.56159C18.8404 8.08221 17.8541 7.8162 16.8466 7.78467C14.6974 7.55907 12.6138 9.07073 11.5186 9.07073C10.4022 9.07073 8.71596 7.80707 6.90016 7.84443C5.72565 7.88237 4.581 8.22391 3.57774 8.83577C2.57448 9.44762 1.74681 10.3089 1.17539 11.3358C-1.29982 15.6213 0.546471 21.9196 2.91756 25.3838C4.10387 27.08 5.49032 28.9748 7.30449 28.9076C9.07975 28.834 9.74278 27.7756 11.8857 27.7756C14.0087 27.7756 14.6307 28.9076 16.4817 28.8649C18.3866 28.834 19.5868 27.1611 20.7315 25.4487C21.5839 24.2401 22.2398 22.9043 22.6749 21.4907C21.5682 21.0226 20.6237 20.2391 19.9593 19.2379C19.2949 18.2366 18.9399 17.062 18.9387 15.8603Z"
                    fill="currentColor"
                  />
                  <path
                    d="M15.4423 5.50662C16.4809 4.25976 16.9926 2.65712 16.8687 1.03906C15.2819 1.20573 13.8161 1.96413 12.7634 3.16315C12.2487 3.7489 11.8546 4.43035 11.6034 5.16855C11.3522 5.90674 11.249 6.68719 11.2996 7.4653C12.0933 7.47347 12.8785 7.30144 13.5961 6.96216C14.3137 6.62289 14.9449 6.12521 15.4423 5.50662Z"
                    fill="currentColor"
                  />
                  <path
                    d="M40.2353 19.5296H34.4095L33.0105 23.6606H30.543L36.061 8.37695H38.6247L44.1427 23.6606H41.6331L40.2353 19.5296ZM35.0129 17.6234H39.6308L37.3543 10.919H37.2906L35.0129 17.6234Z"
                    fill="currentColor"
                  />
                  <path
                    d="M56.0599 18.0912C56.0599 21.5539 54.2065 23.7787 51.4097 23.7787C50.7012 23.8157 49.9966 23.6525 49.3765 23.3077C48.7564 22.9629 48.246 22.4505 47.9037 21.8291H47.8507V27.3483H45.5635V12.519H47.7774V14.3724H47.8195C48.1776 13.754 48.6966 13.2442 49.3214 12.8973C49.9462 12.5504 50.6533 12.3794 51.3676 12.4024C54.1957 12.4025 56.0599 14.638 56.0599 18.0912ZM53.7089 18.0912C53.7089 15.8352 52.5431 14.352 50.7642 14.352C49.0166 14.352 47.8411 15.8664 47.8411 18.0912C47.8411 20.3364 49.0166 21.84 50.7642 21.84C52.5431 21.84 53.7089 20.3676 53.7089 18.0912H53.7089Z"
                    fill="currentColor"
                  />
                  <path
                    d="M68.3252 18.0912C68.3252 21.5539 66.4712 23.7787 63.6743 23.7787C62.9658 23.8157 62.2612 23.6525 61.6411 23.3077C61.0211 22.9629 60.5107 22.4505 60.1683 21.8291H60.1154V27.3483H57.8281V12.519H60.0421V14.3724H60.0841C60.4422 13.754 60.9613 13.2442 61.5861 12.8973C62.2108 12.5504 62.918 12.3794 63.6322 12.4024C66.4604 12.4025 68.3252 14.638 68.3252 18.0912ZM65.9736 18.0912C65.9736 15.8352 64.8077 14.352 63.0289 14.352C61.2813 14.352 60.1058 15.8664 60.1058 18.0912C60.1058 20.3364 61.2813 21.84 63.0289 21.84C64.8077 21.84 65.9736 20.3676 65.9736 18.0912Z"
                    fill="currentColor"
                  />
                  <path
                    d="M76.4293 19.4032C76.5988 20.9188 78.0711 21.9139 80.0831 21.9139C82.011 21.9139 83.398 20.9187 83.398 19.5521C83.398 18.3658 82.5615 17.6555 80.5807 17.1687L78.6 16.6916C75.7935 16.0137 74.4906 14.7012 74.4906 12.5714C74.4906 9.93435 76.7887 8.12305 80.0507 8.12305C83.2815 8.12305 85.4954 9.93435 85.5699 12.5714H83.261C83.1228 11.0461 81.862 10.1255 80.0194 10.1255C78.1769 10.1255 76.9161 11.057 76.9161 12.4127C76.9161 13.4932 77.7214 14.1291 79.6913 14.6158L81.3752 15.0293C84.511 15.7709 85.8127 17.0305 85.8127 19.266C85.8127 22.1254 83.5363 23.9163 79.9136 23.9163C76.5242 23.9163 74.2357 22.1675 74.0879 19.403L76.4293 19.4032Z"
                    fill="currentColor"
                  />
                  <path
                    d="M90.7522 9.88086V12.5179H92.8712V14.3292H90.7522V20.4722C90.7522 21.4265 91.1765 21.8712 92.108 21.8712C92.3596 21.8669 92.6107 21.8492 92.8604 21.8183V23.6188C92.4416 23.6971 92.016 23.7325 91.59 23.7246C89.334 23.7246 88.4542 22.8772 88.4542 20.7162V14.3292H86.834V12.5179H88.4541V9.88086H90.7522Z"
                    fill="currentColor"
                  />
                  <path
                    d="M94.0967 18.09C94.0967 14.584 96.1616 12.3809 99.3815 12.3809C102.612 12.3809 104.668 14.5839 104.668 18.09C104.668 21.6056 102.623 23.7991 99.3815 23.7991C96.1411 23.7991 94.0967 21.6056 94.0967 18.09ZM102.337 18.09C102.337 15.6849 101.235 14.2655 99.3815 14.2655C97.5282 14.2655 96.426 15.6958 96.426 18.09C96.426 20.5047 97.5282 21.9133 99.3815 21.9133C101.235 21.9133 102.337 20.5047 102.337 18.09H102.337Z"
                    fill="currentColor"
                  />
                  <path
                    d="M106.553 12.5189H108.734V14.4155H108.787C108.935 13.8232 109.282 13.2997 109.77 12.9331C110.258 12.5665 110.857 12.379 111.467 12.4023C111.731 12.4014 111.994 12.43 112.251 12.4877V14.6271C111.918 14.5254 111.571 14.4787 111.223 14.4889C110.891 14.4754 110.56 14.534 110.252 14.6606C109.945 14.7872 109.668 14.9789 109.442 15.2225C109.216 15.466 109.045 15.7557 108.941 16.0716C108.837 16.3875 108.802 16.7222 108.84 17.0526V23.6619H106.553L106.553 12.5189Z"
                    fill="currentColor"
                  />
                  <path
                    d="M122.797 20.3881C122.489 22.4109 120.519 23.7991 117.999 23.7991C114.757 23.7991 112.745 21.6272 112.745 18.1429C112.745 14.6477 114.768 12.3809 117.903 12.3809C120.985 12.3809 122.924 14.4986 122.924 17.8773V18.6609H115.054V18.7991C115.018 19.2092 115.069 19.6223 115.205 20.0111C115.34 20.3998 115.557 20.7552 115.841 21.0537C116.124 21.3523 116.468 21.587 116.849 21.7425C117.23 21.898 117.64 21.9705 118.052 21.9554C118.592 22.006 119.135 21.8808 119.598 21.5984C120.062 21.3161 120.422 20.8915 120.625 20.3881L122.797 20.3881ZM115.065 17.0623H120.636C120.656 16.6936 120.6 16.3246 120.472 15.9785C120.343 15.6324 120.144 15.3167 119.887 15.0511C119.63 14.7856 119.322 14.5759 118.98 14.4353C118.639 14.2948 118.272 14.2263 117.903 14.2342C117.53 14.232 117.161 14.3036 116.816 14.4448C116.472 14.586 116.158 14.7941 115.894 15.057C115.631 15.32 115.421 15.6325 115.279 15.9767C115.137 16.3209 115.064 16.6899 115.065 17.0624V17.0623Z"
                    fill="currentColor"
                  />
                </svg>
              </a>
            )}
            {isVisibleGooglePlay && (
              <a
                href={googlePlayLink || GlobalModel.branch_links.google_play}
                className="app-android block border border-white hover:border-gold-300 rounded-lg text-white hover:text-gold-300 px-[9px] py-1.5 mb-3 tablet-m:mb-0 tablet-m:mr-3 desktop:mr-3"
                data-test-id="footer__googleplay_button"
                aria-label="google play"
              >
                <svg
                  className="block h-6.5"
                  viewBox="0 0 114 26"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M56.8 10.8641C54.4178 10.8641 52.5319 12.6641 52.5319 15.1641C52.5319 17.5641 54.4178 19.4641 56.8 19.4641C59.1822 19.4641 61.0682 17.6641 61.0682 15.1641C61.0682 12.5641 59.1822 10.8641 56.8 10.8641ZM56.8 17.6641C55.5096 17.6641 54.4178 16.5641 54.4178 15.0641C54.4178 13.5641 55.5096 12.4641 56.8 12.4641C58.0904 12.4641 59.1822 13.4641 59.1822 15.0641C59.1822 16.5641 58.0904 17.6641 56.8 17.6641ZM47.5689 10.8641C45.1867 10.8641 43.3007 12.6641 43.3007 15.1641C43.3007 17.5641 45.1867 19.4641 47.5689 19.4641C49.9511 19.4641 51.837 17.6641 51.837 15.1641C51.837 12.5641 49.9511 10.8641 47.5689 10.8641ZM47.5689 17.6641C46.2785 17.6641 45.1867 16.5641 45.1867 15.0641C45.1867 13.5641 46.2785 12.4641 47.5689 12.4641C48.8593 12.4641 49.9511 13.4641 49.9511 15.0641C49.9511 16.5641 48.8593 17.6641 47.5689 17.6641ZM36.5511 12.1641V13.9641H40.8193C40.72 14.9641 40.323 15.7641 39.8267 16.2641C39.2311 16.8641 38.2385 17.5641 36.5511 17.5641C33.8711 17.5641 31.8859 15.4641 31.8859 12.7641C31.8859 10.0641 33.9704 7.96406 36.5511 7.96406C37.9407 7.96406 39.0326 8.56406 39.8267 9.26406L41.117 7.96406C40.0252 6.96406 38.6356 6.16406 36.6504 6.16406C33.077 6.16406 30 9.16406 30 12.7641C30 16.3641 33.077 19.3641 36.6504 19.3641C38.6356 19.3641 40.0252 18.7641 41.2163 17.4641C42.4074 16.2641 42.8044 14.5641 42.8044 13.2641C42.8044 12.8641 42.8044 12.4641 42.7052 12.1641H36.5511ZM81.6148 13.5641C81.2178 12.5641 80.2252 10.8641 78.0415 10.8641C75.8578 10.8641 74.0711 12.5641 74.0711 15.1641C74.0711 17.5641 75.8578 19.4641 78.24 19.4641C80.1259 19.4641 81.317 18.2641 81.7141 17.5641L80.3245 16.5641C79.8282 17.2641 79.2326 17.7641 78.24 17.7641C77.2474 17.7641 76.6519 17.3641 76.1556 16.4641L81.8133 14.0641L81.6148 13.5641ZM75.8578 14.9641C75.8578 13.3641 77.1482 12.4641 78.0415 12.4641C78.7363 12.4641 79.4311 12.8641 79.6296 13.3641L75.8578 14.9641ZM71.1926 19.0641H73.0785V6.56406H71.1926V19.0641ZM68.2148 11.7641C67.7185 11.2641 66.9244 10.7641 65.9319 10.7641C63.8474 10.7641 61.8622 12.6641 61.8622 15.0641C61.8622 17.4641 63.7482 19.2641 65.9319 19.2641C66.9244 19.2641 67.7185 18.7641 68.1156 18.2641H68.2148V18.8641C68.2148 20.4641 67.3215 21.3641 65.9319 21.3641C64.84 21.3641 64.0459 20.5641 63.8474 19.8641L62.2593 20.5641C62.7556 21.6641 63.9467 23.0641 66.0311 23.0641C68.2148 23.0641 70.0015 21.7641 70.0015 18.6641V11.0641H68.2148V11.7641ZM66.0311 17.6641C64.7407 17.6641 63.6489 16.5641 63.6489 15.0641C63.6489 13.5641 64.7407 12.4641 66.0311 12.4641C67.3215 12.4641 68.3141 13.5641 68.3141 15.0641C68.3141 16.5641 67.3215 17.6641 66.0311 17.6641ZM90.2504 6.56406H85.7837V19.0641H87.6696V14.3641H90.2504C92.3348 14.3641 94.32 12.8641 94.32 10.4641C94.32 8.06406 92.3348 6.56406 90.2504 6.56406ZM90.3496 12.5641H87.6696V8.26406H90.3496C91.7393 8.26406 92.5333 9.46406 92.5333 10.3641C92.4341 11.4641 91.64 12.5641 90.3496 12.5641ZM101.764 10.7641C100.375 10.7641 98.9852 11.3641 98.4889 12.6641L100.176 13.3641C100.573 12.6641 101.169 12.4641 101.864 12.4641C102.856 12.4641 103.75 13.0641 103.849 14.0641V14.1641C103.551 13.9641 102.757 13.6641 101.963 13.6641C100.176 13.6641 98.3896 14.6641 98.3896 16.4641C98.3896 18.1641 99.8785 19.2641 101.467 19.2641C102.757 19.2641 103.353 18.6641 103.849 18.0641H103.948V19.0641H105.735V14.2641C105.536 12.0641 103.849 10.7641 101.764 10.7641ZM101.566 17.6641C100.97 17.6641 100.077 17.3641 100.077 16.5641C100.077 15.5641 101.169 15.2641 102.062 15.2641C102.856 15.2641 103.253 15.4641 103.75 15.6641C103.551 16.8641 102.559 17.6641 101.566 17.6641ZM111.988 11.0641L109.904 16.4641H109.804L107.621 11.0641H105.636L108.911 18.6641L107.025 22.8641H108.911L113.973 11.0641H111.988ZM95.3126 19.0641H97.1985V6.56406H95.3126V19.0641Z"
                    fill="currentColor"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M15.9278 8.93516L2.11721 1.0253L2.11475 1.02392C1.78621 0.840428 1.53142 0.776697 1.3404 0.772949L12.7305 12.1563L15.9278 8.93516ZM0.467831 1.49649C0.441385 1.61738 0.425781 1.75312 0.425781 1.90286V24.0029C0.425781 24.2198 0.458526 24.4073 0.509542 24.5629L11.8906 13.0027L0.467831 1.49649ZM21.4653 12.2226L21.4581 12.2184L16.995 9.56557L13.5643 13.0116L16.9191 16.5591L21.4713 14.0826C22.0522 13.7464 22.2079 13.3891 22.2079 13.1543C22.2079 12.9187 22.0511 12.5598 21.4653 12.2226ZM1.37994 25.2888L12.7221 13.862L15.8462 17.1616L2.13782 25.0691C1.82778 25.2054 1.57819 25.2716 1.37994 25.2888Z"
                    fill="currentColor"
                  />
                </svg>
              </a>
            )}
            {isVisibleGooglePlay && (
              <a
                href={appGalleryLink || GlobalModel.branch_links.app_gallery}
                className="app-android block border border-white hover:border-gold-300 rounded-lg text-white hover:text-gold-300 px-[9px] py-1.5 mb-3 tablet-m:mb-0"
                data-test-id="footer__appgallery_button"
                aria-label="app gallery"
              >
                <svg
                  className="block h-6.5"
                  viewBox="0 0 116 26"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M37.8707 14.7964L35.6633 8.64384L33.4662 14.7964H37.8707ZM30.1758 19.3614L34.7516 6.99254H36.5853L41.1611 19.3614H39.4714L38.378 16.1861H32.9795L31.8861 19.3614H30.1758ZM43.8223 17.9719V22.8904H42.345V10.0831H43.8223V11.4834C44.1342 10.9317 44.525 10.5251 44.998 10.2599C45.4778 9.98764 46.0434 9.85327 46.698 9.85327C47.7914 9.85327 48.6792 10.3023 49.3613 11.2005C50.0502 12.0915 50.393 13.269 50.393 14.7329C50.393 16.1897 50.0502 17.3637 49.3613 18.2653C48.6792 19.1564 47.7914 19.6019 46.698 19.6019C46.0434 19.6019 45.4778 19.4711 44.998 19.2059C44.525 18.9336 44.1342 18.5199 43.8223 17.9719ZM48.854 14.7329C48.854 13.612 48.6278 12.7315 48.1753 12.0915C47.7366 11.4515 47.1265 11.1298 46.3416 11.1298C45.5601 11.1298 44.9397 11.4515 44.4873 12.0915C44.0417 12.7315 43.8189 13.612 43.8189 14.7329C43.8189 15.8538 44.0417 16.7343 44.4873 17.3743C44.9397 18.0072 45.5567 18.3255 46.3416 18.3255C47.1231 18.3255 47.7366 18.0072 48.1753 17.3743C48.6278 16.7343 48.854 15.8538 48.854 14.7329ZM53.4911 17.9715V22.8901H52.0036V10.0828H53.4911V11.483C53.803 10.9314 54.1904 10.5248 54.6565 10.2596C55.1364 9.98732 55.7053 9.85295 56.3669 9.85295C57.4534 9.85295 58.3377 10.302 59.0198 11.2002C59.7087 12.0912 60.0515 13.2687 60.0515 14.7326C60.0515 16.1894 59.7087 17.3634 59.0198 18.265C58.3377 19.1561 57.4534 19.6016 56.3669 19.6016C55.7053 19.6016 55.1364 19.4708 54.6565 19.2056C54.1904 18.9333 53.7996 18.5196 53.4911 17.9715ZM58.5125 14.7326C58.5125 13.6117 58.2863 12.7312 57.8339 12.0912C57.3951 11.4512 56.785 11.1294 56.0001 11.1294C55.2186 11.1294 54.5982 11.4512 54.1458 12.0912C53.7071 12.7312 53.4877 13.6117 53.4877 14.7326C53.4877 15.8535 53.7071 16.734 54.1458 17.374C54.5982 18.0069 55.2152 18.3251 56.0001 18.3251C56.7816 18.3251 57.3951 18.0069 57.8339 17.374C58.2863 16.734 58.5125 15.8535 58.5125 14.7326ZM70.6838 17.5967V14.2765H68.0412V12.8974H72.2948V18.2155C71.6675 18.6752 70.9752 19.0217 70.2177 19.2586C69.467 19.4885 68.665 19.6016 67.8081 19.6016C65.9298 19.6016 64.4628 19.0359 63.4037 17.9079C62.3514 16.7799 61.8235 15.2064 61.8235 13.1874C61.8235 11.1683 62.3514 9.59483 63.4037 8.46685C64.4628 7.33181 65.9332 6.76252 67.8081 6.76252C68.5896 6.76252 69.3299 6.86506 70.0257 7.06661C70.7352 7.26109 71.3865 7.55457 71.9794 7.94353V9.7292C71.3796 9.1988 70.7421 8.80277 70.0668 8.53757C69.3916 8.27237 68.6821 8.14154 67.9417 8.14154C66.4782 8.14154 65.3745 8.56233 64.6307 9.40389C63.9007 10.2525 63.5373 11.5149 63.5373 13.1838C63.5373 14.8528 63.9007 16.1116 64.6307 16.9532C65.3745 17.7947 66.4782 18.2155 67.9417 18.2155C68.5142 18.2155 69.0283 18.166 69.4807 18.0706C69.9263 17.9715 70.3273 17.8124 70.6838 17.5967ZM80.2385 14.6905H78.7715C77.5753 14.6905 76.7492 14.8319 76.2899 15.1183C75.8306 15.3977 75.601 15.8786 75.601 16.561C75.601 17.1055 75.7724 17.5369 76.1185 17.8552C76.4613 18.1699 76.9309 18.3254 77.5273 18.3254C78.3499 18.3254 79.008 18.0249 79.5016 17.4273C79.9951 16.8297 80.2419 16.0306 80.2419 15.0335V14.6905H80.2385ZM81.7158 14.0646V19.3615H80.2385V17.9506C79.9026 18.5164 79.481 18.9336 78.984 19.2059C78.4767 19.4711 77.8598 19.6019 77.1297 19.6019C76.2111 19.6019 75.4776 19.3367 74.9326 18.8063C74.3911 18.2689 74.1237 17.5511 74.1237 16.6529C74.1237 15.6169 74.4596 14.8284 75.1348 14.2909C75.8169 13.7605 76.828 13.4953 78.1717 13.4953H80.2385V13.3503C80.2385 12.6467 80.0157 12.1021 79.5701 11.7202C79.1245 11.3384 78.4973 11.1474 77.6884 11.1474C77.1742 11.1474 76.6772 11.2111 76.1905 11.3348C75.7038 11.4586 75.2342 11.6531 74.7818 11.9077V10.4968C75.3233 10.2811 75.8478 10.122 76.3619 10.0159C76.8692 9.90276 77.3593 9.84972 77.8392 9.84972C79.1417 9.84972 80.1151 10.1962 80.756 10.8928C81.397 11.5965 81.7158 12.6502 81.7158 14.0646ZM84.1457 19.3613V6.47266H85.6229V19.3613H84.1457ZM88.0401 19.3613V6.47266H89.5174V19.3613H88.0401ZM99.7767 14.3367V15.0898H92.9833C93.045 16.1329 93.3466 16.9356 93.895 17.4907C94.4468 18.0353 95.2146 18.304 96.1949 18.304C96.7605 18.304 97.3089 18.2333 97.8367 18.0954C98.3783 17.9504 98.9061 17.7347 99.4271 17.4483V18.891C98.8993 19.1208 98.3577 19.2976 97.7956 19.4249C97.2437 19.5416 96.6782 19.6017 96.1058 19.6017C94.673 19.6017 93.5385 19.1703 92.6919 18.3076C91.8556 17.4518 91.4374 16.2885 91.4374 14.8176C91.4374 13.2935 91.835 12.0878 92.6337 11.2038C93.4289 10.3057 94.5051 9.85658 95.8521 9.85658C97.0621 9.85658 98.0149 10.2597 98.7176 11.0694C99.4237 11.8685 99.7767 12.9576 99.7767 14.3367ZM93.0244 13.8982H98.2994C98.2857 13.0637 98.0561 12.3954 97.6105 11.8933C97.1718 11.3983 96.5891 11.1507 95.859 11.1507C95.0364 11.1507 94.3749 11.3912 93.8744 11.8721C93.3809 12.353 93.0998 13.0283 93.0244 13.8982ZM106.746 9.93791L106.756 11.5043C106.588 11.4053 106.406 11.3346 106.207 11.2851C106.009 11.2356 105.8 11.2109 105.57 11.2109C104.734 11.2109 104.089 11.4937 103.637 12.056C103.191 12.6146 102.968 13.4173 102.968 14.4675V19.3577H101.491V10.0829H102.968V11.5256C103.28 10.9598 103.685 10.5426 104.185 10.2703C104.686 9.99095 105.289 9.85304 105.998 9.85304C106.101 9.85304 106.214 9.86366 106.341 9.88487C106.461 9.89194 106.598 9.90962 106.746 9.93791ZM116 10.0829L112.051 20.217C111.633 21.3237 111.225 22.0486 110.828 22.3881C110.43 22.724 109.899 22.8902 109.237 22.8902H108.051V21.6172H108.922C109.327 21.6172 109.645 21.5147 109.875 21.3131C110.098 21.1187 110.345 20.6484 110.615 19.9023L110.89 19.2128L107.243 10.0829H108.812L111.626 17.3316L114.43 10.0829H116Z"
                    fill="currentColor"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M6.51099 1H16.6155C21.3816 1 23.117 2.80171 23.117 7.7499V18.2403C23.117 23.1885 21.3722 25 16.6155 25H6.51099C1.74486 25 0 23.1885 0 18.2403V7.7499C0 2.80171 1.74486 1 6.51099 1ZM9.58738 16.1288L9.86719 15.4825H11.2442L11.5146 16.1288H12.1559L10.8292 12.9954H10.2916L8.96489 16.1288H9.58738ZM11.0147 14.9146H10.0873L10.5494 13.7951L11.0147 14.9146ZM20.547 12.9954H19.9339V16.1288H20.547V12.9954ZM19.2108 15.5609H17.5289V14.7873H18.6449V14.2096H17.5289V13.5731H19.1448V12.9954H16.9252V16.1288H19.2108V15.5609ZM16.4316 12.9954H15.7903L15.1238 15.1561L14.4353 12.9954H13.9322L13.2532 15.1561L12.5835 12.9954H11.9233L12.9828 16.1386H13.4858L14.1743 14.0758L14.8628 16.1386H15.3816L16.4316 12.9954ZM8.6505 12.9954H8.03745V14.7938C8.03745 15.303 7.79537 15.5837 7.34893 15.5837C6.9025 15.5837 6.66042 15.303 6.66042 14.7743V12.9987H6.04736V14.7906C6.04736 15.6751 6.52209 16.1876 7.3395 16.1876C8.17578 16.1876 8.6505 15.6686 8.6505 14.771V12.9954ZM5.19537 12.9954H4.58231V14.2586H3.199V12.9954H2.58594V16.1386H3.199V14.8559H4.58231V16.1386H5.19537V12.9954ZM7.71991 5.01172C7.71991 7.21164 9.43648 9.00356 11.5649 9.00356C13.6839 9.00356 15.4099 7.20837 15.4099 5.01172H14.8723C14.8723 6.90156 13.3852 8.43563 11.5649 8.43563C9.74458 8.43563 8.25752 6.90156 8.25752 5.01172H7.71991Z"
                    fill="currentColor"
                  />
                </svg>
              </a>
            )}
          </div>
        </div>

        <div className="footer__langswitcher tablet-m:flex tablet-m:flex-row tablet-m:flex-end tablet-m:justify-end -ml-2 tablet-m:ml-0 tablet-m:mt-[-10px] desktop:mt-0 tablet-m:-mr-3 desktop:ml-3 desktop:ml-[47px] desktop:mt-[-9px] z-[10]">
          <LanguageSwitcher
            theme="dark"
            direction="top"
            chinese={!!chinese}
            accessLanguages={accessLanguages}
          />
        </div>

        <Contacts
          classes={{
            container:
              "footer__socials flex flex-row justify-end items-center tablet-m:items-end desktop:justify-start",
            link: "hover:text-gold-300",
          }}
        />

        <div
          className="footer__navbar desktop:max-w-[520px] desktop:justify-self-end flex flex-col flex-wrap justify-start py-8 desktop:py-0 my-10 desktop:my-0 desktop:ml-4 border-y border-gray-100 desktop:border-0 text-base leading-5 tracking-normal tablet-m:text-[18px] tablet-m:leading-[22px] overflow-auto gap-y-5 gap-x-2 tablet-s:gap-x-12 desktop:gap-x-16 [&>a]:select-none [&>a:hover]:text-gold-300 [&>a:active]:text-gold-300"
          ref={navbarRef}
        >
          <RouterLink
            key="/guidelines"
            uri="/guidelines"
            availableInChinese
            data-test-id="footer__guidelines_link"
          >
            {GlobalModel.getTemplate("menu.guidelines")}
          </RouterLink>
          <RouterLink
            key="/security"
            uri="/security"
            availableInChinese
            data-test-id="footer__security_link"
          >
            {GlobalModel.getTemplate("menu.security")}
          </RouterLink>
          <RouterLink
            key="/terms"
            uri="/terms"
            data-test-id="footer__terms_link"
          >
            {GlobalModel.getTemplate("menu.terms")}
          </RouterLink>
          <RouterLink
            key="/legal/cookies"
            uri="/legal/cookies"
            data-test-id="footer__cookie_link"
          >
            {GlobalModel.getTemplate("menu.cookie")}
          </RouterLink>
          <RouterLink
            key="/privacy"
            uri="/privacy"
            data-test-id="footer__privacy_link"
          >
            {GlobalModel.getTemplate("menu.privacy")}
          </RouterLink>
          {showPressLink && (
            <a href={pressLink.url} data-test-id="footer__press_link">
              {GlobalModel.getTemplate("menu.press")}
            </a>
          )}
          <RouterLink
            key="/faq"
            uri="/faq"
            availableInChinese
            data-test-id="footer__faq_link"
          >
            {GlobalModel.getTemplate("menu.faq")}
          </RouterLink>
          <RouterLink
            key="/legal/consumer-health-data-privacy-policy"
            uri="/legal/consumer-health-data-privacy-policy"
            availableInChinese
            data-test-id="footer__consumer_health_link"
            lang={GlobalModel.lang}
            className={classNames(GlobalModel.lang === "de" && "hyphens-de")}
          >
            {GlobalModel.getTemplate("menu.consumerHealth")}
          </RouterLink>
          <RouterLink
            key="/contact-us"
            uri="/contact-us"
            availableInChinese
            data-test-id="footer__contact_us_link"
          >
            {GlobalModel.getTemplate("menu.contactUs")}
          </RouterLink>
          {showSharePersonalInfoButton && (
            <a
              key={pressLink.url}
              data-test-id="footer__share_personal"
              className="cky-banner-element cursor-pointer"
            >
              {GlobalModel.getTemplate("menu.sharePersonalInfo")}
            </a>
          )}
        </div>

        <div className="footer__copyright font-serif-italic text-center italic text-[14px] leading-[18px] desktop:text-left desktop:self-end desktop:ml-8 tablet-m:mb-0">
          ©&nbsp;
          <span className="current-year">{currentYear}</span> PURE.&nbsp;
          {companyName}
        </div>
      </div>
    </footer>
  )
})
